'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/tailwind'
import type { Icon as PhosphorIcon } from '@phosphor-icons/react'
import { ICON_SIZES, FOCUS_STYLES } from './design-tokens'

/**
 * Compact tag variants using CVA for type-safe styling
 * Inspired by Linear's dense, information-rich UI
 */
const compactTagVariants = cva(
  // Base classes
  'inline-flex items-center font-medium transition-colors',
  {
    variants: {
      variant: {
        /** Filled/active state - for selected items */
        filled: 'bg-secondary text-secondary-foreground',
        /** Dashed/inactive state - for placeholders, add buttons */
        dashed:
          'border border-dashed border-muted-foreground/30 text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground',
        /** Ghost - no background, subtle hover */
        ghost: 'text-muted-foreground hover:text-foreground hover:bg-muted',
        /** Colored - uses custom colors (via className) */
        colored: '',
      },
      size: {
        /** Default compact size (h-5, 11px text) */
        default: 'h-5 gap-0.5 rounded px-1.5 text-[11px]',
        /** Slightly larger (h-6, 12px text) */
        sm: 'h-6 gap-1 rounded px-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'filled',
      size: 'default',
    },
  },
)

export interface CompactTagProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof compactTagVariants> {
  /** Optional Phosphor icon to display before the label */
  Icon?: PhosphorIcon
  /** Icon weight - 'bold' for active, 'regular' for inactive */
  iconWeight?: 'regular' | 'bold' | 'fill' | 'duotone'
  /** Whether the tag is in an active/selected state */
  active?: boolean
  /** Whether the tag is disabled */
  disabled?: boolean
  /** Whether this is a clickable button */
  asButton?: boolean
  /** Click handler (only when asButton is true) */
  onClick?: (e: React.MouseEvent) => void
  /** Maximum width before truncation */
  maxWidth?: string
}

/**
 * Compact tag component for dense, Linear-inspired UI.
 *
 * Use for:
 * - Property badges (mood, priority, category)
 * - Selected items in multi-selects
 * - Type indicators
 * - Inline metadata
 *
 * @example
 * // Filled tag with icon
 * <CompactTag Icon={Tag} iconWeight="bold">Daily Reflection</CompactTag>
 *
 * // Dashed placeholder
 * <CompactTag variant="dashed" Icon={Plus}>Add person</CompactTag>
 *
 * // Clickable tag
 * <CompactTag asButton onClick={handleClick} Icon={User}>John</CompactTag>
 */
export function CompactTag({
  className,
  variant,
  size,
  Icon,
  iconWeight = 'regular',
  active,
  disabled,
  asButton = false,
  onClick,
  maxWidth,
  children,
  ...props
}: CompactTagProps) {
  // Auto-select variant based on active prop if not explicitly set
  const effectiveVariant = variant ?? (active ? 'filled' : 'dashed')
  const effectiveIconWeight = iconWeight ?? (active ? 'bold' : 'regular')

  const Component = asButton ? 'button' : 'span'

  const content = (
    <>
      {Icon && (
        <Icon
          className={cn(ICON_SIZES.compact, 'shrink-0')}
          weight={effectiveIconWeight}
        />
      )}
      {children && (
        <span className={cn('truncate', maxWidth && `max-w-[${maxWidth}]`)}>
          {children}
        </span>
      )}
    </>
  )

  if (asButton) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          compactTagVariants({ variant: effectiveVariant, size }),
          FOCUS_STYLES.ringCompact,
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    )
  }

  return (
    <span
      className={cn(
        compactTagVariants({ variant: effectiveVariant, size }),
        disabled && 'opacity-50',
        className,
      )}
      {...props}
    >
      {content}
    </span>
  )
}

/**
 * Preset: Metadata text with consistent styling
 */
export function MetaText({
  className,
  children,
  faded = false,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { faded?: boolean }) {
  return (
    <span
      className={cn(
        'text-xs',
        faded ? 'text-muted-foreground/60' : 'text-muted-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

export { compactTagVariants }
